import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormTemplate,
  FormTemplateSection
} from 'data/template/template.types';
import { SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import GridGroupHeader from 'components/GridGroupHeader';
import { JAreaAfRowData, JurisdictionArea } from 'data/jurisdictionArea/jurisdictionArea.types';
import CommonGrid from 'shared/commonGrid';
import Form, { FieldUpdateHandlerParam } from 'shared/form';
import './jurisdiction-area-form.scss';
import { FormLayout } from 'components/FormLayout';
import { ROWSELECTION } from 'shared/commonGrid/grid-enums';

type Props = {
  template: FormTemplate,
  data?: JAreaAfRowData[],
  i18nKeyPrefix?: string
  gridData: JAreaAfRowData[],
  onFieldUpdate?: (param: FieldUpdateHandlerParam, formSection?: 'main') => void,
  onSelectionChanged: (event: SelectionChangedEvent) => void,
  defaultsSelected?: JAreaAfRowData[],
  selectedJurisdictionArea?: JAreaAfRowData[],
  mode: 'create' | 'edit' | 'view',
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => JurisdictionArea
}

export const COLLECTION_FUNCTION_GROUP_I18N_KEY = 'jurisdictionArea.form';

export const JurisdictionAreaForm = forwardRef<Ref, Props>(({
  template,
  i18nKeyPrefix = COLLECTION_FUNCTION_GROUP_I18N_KEY,
  onFieldUpdate = () => null,
  gridData,
  onSelectionChanged = () => null,
  defaultsSelected,
  selectedJurisdictionArea,
  mode,
}: Props, ref) => {
  const mainRef = useRef<Form>(null);
  const [defaultSelection, setDefaultSelection] = useState<JAreaAfRowData[]>([]);

  useEffect(() => {
    if (defaultsSelected?.length && gridData) {
      const selections: JAreaAfRowData[] = gridData.filter((data: any, index: number) => {
        return defaultsSelected?.find((defaultSelected) => defaultSelected.AFNAME === data.AFNAME);
      })
      setDefaultSelection(selections);
    }
  }, [defaultsSelected, gridData]);

  const isValid = (forceValidation = false, forceTouched = true) => {
    const isMainValid = mainRef.current?.isValid(forceValidation, forceTouched) ?? true;
    return isMainValid;
  };

  const getValues = () => {
    const formValues: JurisdictionArea = {
      ...mainRef.current?.getValues(),
    };

    return formValues;
  };

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
  }));

  const { t } = useTranslation();

  const autoGroupColumnDefForEdit = {
    headerName: 'Access Function Type / Access Function',
    field: 'AFNAME',
    minWidth: 250,
    cellRenderer: 'agGroupCellRenderer',
    cellRendererParams: {
      checkbox: mode !== 'view'
    },
    filter: 'agTextColumnFilter',
    sortable: true,
    floatingFilter: true,
    collapsable: true,
    resizable: true,
    lockVisible: true,
    headerComponentFramework: GridGroupHeader,
    headerCheckboxSelection: mode !== 'view'
  };

  const defaultColDef = {
    flex: 1,
    resizable: true,
    filter: 'agTextColumnFilter',
    sortable: true,
    floatingFilter: true,
    lockVisible: true
  };

  const [columnDefs] = useState([
    {
      field: 'AFTYPE',
      rowGroup: true,
      hide: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellRendererParams: {
        checkbox: true
      },
      lockVisible: true,
      checkboxSelection: true
    }
  ]);

  const onMainFieldUpdate = (param: FieldUpdateHandlerParam) =>
    onFieldUpdate(param, 'main');

  return (
    <div className="utimaco-packet-switch-form" data-testid="JurisdictionAreaForm">
      <div data-testid="JurisdictionAreaFormMain">
        <FormLayout>
          <Form
            ref={mainRef}
            name={template.formId ?? template.key}
            defaults={selectedJurisdictionArea ? selectedJurisdictionArea[0] : gridData[0]}
            fields={(template?.main as FormTemplateSection).fields}
            layout={(template?.main as FormTemplateSection).metadata.layout?.rows}
            i18nKeyPrefix={i18nKeyPrefix}
            fieldUpdateHandler={onMainFieldUpdate}
          />
          <div style={{ height: '560px', width: '80%' }} data-testid="AvailableAccessFunctions">
            <CommonGrid
              rowData={gridData}
              noResults={false}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              autoGroupColumnDef={autoGroupColumnDefForEdit}
              rowSelection={ROWSELECTION.MULTIPLE}
              groupSelectsChildren
              suppressAggFuncInHeader
              suppressRowClickSelection
              onSelectionChanged={onSelectionChanged}
              hideSearchBar
              defaultSelections={defaultSelection}
            >
              <h3>{t('jurisdictionArea.form.allAvailableAccessFunction')}</h3>
              <span className="jurisdiction-area-form-note">
                {t('jurisdictionArea.form.note')}
              </span>
            </CommonGrid>
          </div>
        </FormLayout>
      </div>
    </div>
  );
});

export default JurisdictionAreaForm;
